<template>
    <div class="container flashcards-list">
        <div class="">
            <div class="flex justify-between items-center mb-2 mt-6">
                <div class="flex-1 w-full">
                    <h2 class="font-body-1-medium text-content-primary">
                        <span class="md:hidden">{{
                            $t('flashcards:detailsCardList.shortHeader', {
                                count: totalOfCards,
                            })
                        }}</span>
                        <span class="hidden md:block">{{
                            $t('flashcards:detailsCardList.header', {
                                count: totalOfCards,
                            })
                        }}</span>
                    </h2>
                </div>
                <div class="flex-1 flex justify-end w-full">
                    <BtnMenu
                        v-if="isAuth && userHasVisited"
                        variant="outlined"
                        :label="sortingOrder.label || $t('detailsCardList.sorting.original')"
                        :options="sortingOptions"
                        right-icon="sorting"
                        @click:option="(option) => (sortingOrder = option)"
                    />
                </div>
            </div>

            <div
                v-if="isRefreshingCards"
                class="w-full text-center py-30"
                v-html="$t('flashcards:detailsCardList.spinner')"
            ></div>

            <div
                v-for="card in cards"
                :key="card.id"
            >
                <div class="w-full flex flex-col bg-white mb-2 p-3 rounded-lg shadow md:flex-row">
                    <div class="w-full md:w-10/12">
                        <div
                            class="flex flex-col md:flex-row items-center divide-y space-y-1 md:space-y-0 divide-gray-100 md:divide-y-0 md:divide-x md:space-x-6"
                        >
                            <div class="card-container min-w-0">
                                <div class="flex flex-col h-full">
                                    <div
                                        v-if="card.term_image"
                                        class="card-image"
                                        @click="lightboxImage = card.term_image"
                                    >
                                        <img
                                            v-lazy="card.term_image.thumb_file_path"
                                            class="rounded w-full"
                                        />

                                        <span
                                            class="absolute bg-black flex inline-flex items-center mr-1 mt-1 opacity-25 p-1 right-0 rounded-lg top-0"
                                        >
                                            <Btn
                                                icon="maximize"
                                                size="xxs"
                                                compact
                                                color="white"
                                                variant="tonal"
                                            />
                                        </span>
                                    </div>
                                    <div
                                        class="text-body-2-medium text-content-primary flashcard-text-wrapper w-full"
                                        v-html="card.term"
                                    ></div>
                                </div>
                            </div>
                            <div class="md:pl-6 card-container min-w-0">
                                <div class="flex flex-col h-full">
                                    <div
                                        v-if="card.definition_image"
                                        class="card-image order-2 md:order-1"
                                        @click="lightboxImage = card.definition_image"
                                    >
                                        <img
                                            v-lazy="card.definition_image.thumb_file_path"
                                            class="rounded w-full"
                                        />

                                        <span
                                            class="absolute bg-black flex inline-flex items-center mr-1 mt-1 opacity-25 p-1 right-0 rounded-lg top-0"
                                        >
                                            <Btn
                                                icon="maximize"
                                                size="xxs"
                                                compact
                                                color="white"
                                                variant="tonal"
                                            />
                                        </span>
                                    </div>
                                    <div
                                        class="flashcard-text-wrapper body-2-regular text-content-secondary w-full order-1"
                                        v-html="card.definition"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="ml-auto mt-4 md:mt-0">
                        <div class="flashcard-actions items-center space-x-3 flex justify-end">
                            <Btn
                                v-if="isAuth"
                                :variant="BtnVariant.TEXT"
                                compact
                                left-icon="bookmark"
                                :content-color="card.is_bookmarked ? 'yellow-300' : 'black'"
                                @click="toggleBookmark(card)"
                            />
                            <SelectAssessment
                                v-if="isAuth && userHasVisited && card.assessment_id"
                                :model-value="card.assessment_id"
                                @input="updateCardAssessment(card, $event)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="!reachedLastPage"
                class="w-full"
            >
                <Btn
                    v-if="isLoading == false"
                    :variant="BtnVariant.OUTLINED"
                    class="mx-auto"
                    :label="$t('flashcards:detailsCardList.show_more')"
                    @click="getCards"
                />
                <div
                    v-else
                    v-html="$t('flashcards:detailsCardList.spinner')"
                ></div>
            </div>
            <!-- IMPORTANT: With SDFE-642 we introduced permament redirects which made the page inaccessible to some users.
                With `?` we can bust the browser cache and force the page to load the original URL. -->
            <BtnCta
                :href="`${flashcardSet.link_study_page}?`"
                :label="$t('flashcards:detailsCardList.study')"
                class="mb-6 md:mb-1 protected-auth float-right mt-4 w-full md:w-auto"
            />
        </div>
        <Lightbox
            v-if="lightboxImage"
            :model-value="true"
            :sources="[lightboxImage.large_file_path]"
            @update:model-value="lightboxImage = undefined"
        />
    </div>
</template>

<script>
import { get } from 'lodash-es';
import { BtnVariant } from '@components/Btn';
import SelectAssessment from '@components/SelectAssessment';
import Lightbox from '@/components/Lightbox';
import { getAssessmentById } from '@/utils/flashcardUtils';
// eslint-disable-next-line import/no-restricted-paths
import { backend } from '@/api/backend';
import { reactive } from 'vue';

export default {
    components: {
        Lightbox,
        SelectAssessment,
    },
    props: {
        flashcardSet: {
            required: true,
            type: Object,
        },
    },
    i18nOptions: {
        namespaces: ['flashcards'],
    },
    data() {
        return {
            BtnVariant,
            cards: reactive([]),
            lightboxImage: undefined,
            isLoading: false,
            isRefreshingCards: true,
            nextPage: 1,
            numberOfPages: 1,
            reachedLastPage: true,
            sortingOrder: {
                value: '',
            },
        };
    },
    computed: {
        sortingOptions() {
            return [
                {
                    label: this.$t('detailsCardList.sorting.original'),
                    value: '',
                },
                {
                    label: this.$t('detailsCardList.sorting.progress'),
                    value: 'assessment',
                },
            ];
        },
        isAuth() {
            return get(sdWindow, 'flashcards.user');
        },
        userHasVisited() {
            return get(sdWindow, 'flashcards.user.set_data.has_visited');
        },
        totalOfCards() {
            return this.cards.length;
        },
        studyPageUrl() {
            return get(sdWindow, 'flashcards.set.link_study_page', '#');
        },
    },
    watch: {
        // vue-select triggers an 'onChange' event when the
        // component is rendered; to avoid calling the API twice
        // when the page is loaded we use a watched property
        // to assure that the value has really changed
        sortingOrder() {
            this.resetList();
        },
    },
    created() {
        this.getCards();
    },
    methods: {
        getCards() {
            const url = `/flashcards/sets/${this.flashcardSet.id}/cards`;
            const page = this.nextPage;
            const order = this.sortingOrder.value;

            this.isLoading = true;

            backend.get(url, { params: { page, order_by: order } }).then((response) => {
                response = response.data.cards;

                this.cards.push(...response.data);

                this.cards = this.cards.map((card) => {
                    card.images.forEach((image) => {
                        card[`${image.card_side}_image`] = {
                            id: image.id,
                            large_file_path: image.large_file_path,
                            thumb_file_path: image.thumb_file_path,
                        };
                    });
                    return card;
                });

                this.numberOfPages = response.last_page;
                this.nextPage = response.current_page + 1;
                this.reachedLastPage = this.nextPage > this.numberOfPages;

                this.isRefreshingCards = false;
                this.isLoading = false;
            });
        },
        getIconClass(assessmentName) {
            switch (assessmentName) {
                case 'correct':
                    return 'icon-checkmark-outlined';
                case 'incorrect':
                    return 'icon-incorrect';
                case 'unsure':
                    return 'icon-approximately';
                case 'hidden':
                    return 'icon-visibility-off';
            }
        },
        updateCardAssessment(card, assId) {
            const url = `/flashcards/sets/${this.flashcardSet.id}/cards/${card.id}/assess`;

            // check if the value has changed to avoid making
            // an unnecessary API call when the page is loaded,
            // as vue-select triggers an 'onChange' event
            // when the component is rendered.
            if (assId == card.assessment_id) {
                return;
            }

            const assessment = getAssessmentById(assId);
            card.assessment_id = assessment.id;
            card.assessment_name = assessment.name;
            card.assessment_order = assessment.order;

            backend
                .post(url, { assessment_id: card.assessment_id })
                .then(() => this.$bus.emit('flashcards-update-stats'))
                .catch((error) => console.error('Could not update the card assessment'));
        },
        toggleBookmark(card) {
            const url = `/flashcards/sets/${this.flashcardSet.id}/cards/${card.id}/bookmark`;
            this.cards = this.cards.map((c) => (c.id === card.id ? { ...c, is_bookmarked: !c.is_bookmarked } : c));
            backend.post(url).catch((error) => console.error('Could not bookmark the card'));
        },
        resetList() {
            this.nextPage = 1;
            this.numberOfPages = 1;
            this.isRefreshingCards = true;
            this.reachedLastPage = true;
            this.cards = [];

            this.getCards();
        },
        checkUserCanStudy(event) {
            if (!this.isAuth) {
                event.preventDefault();
                this.$bus.emit('prompt-signup', {});
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.card-container {
    @apply flex-1 w-full break-words;
    .card-image {
        @apply cursor-pointer relative mb-4 md:mb-3  w-full md:w-16;
    }
}
.flashcard-text-wrapper {
    :deep() {
        ol,
        ul {
            @apply list-inside;
        }

        ol {
            @apply list-decimal;
        }

        ul {
            @apply list-disc;
        }
        a {
            @apply text-blue-600;
        }
    }
}
</style>
